/**
 * Pure styling primitives for the trail polyline layer.
 *
 * Stream A PR 2 / commit M5 — REQ-UI-012 polyline of recent
 * positions for moving Producers. ADR-016 Open Item 12 ("trail
 * visual design TBD") stays OPEN; this module ships a **sensible
 * default** per the M5 brief — a clean polyline keyed off the
 * selected Producer's most-recent ``health_state``. Visual
 * refinement (gradient tails, dashed/muted treatments for
 * confidence-degraded, recent-positions-brighter) is post-MVP per
 * ADR-016 §5 and the brief's "defer the visual refinement".
 *
 * # Visual default (M5)
 *
 *   - Solid line, 2 px width, status-color matching the most-
 *     recent point's ``health_state`` (so a degraded Producer's
 *     trail reads degraded; an offline Producer's trail reads
 *     unhealthy).
 *   - 0.8 opacity so the trail reads as "recent past" rather than
 *     "current state" — the marker (rendered above the trail) is
 *     the fully-opaque current-state surface.
 *   - Round line-cap + line-join — the storyboard storyboards
 *     (Flow 02) describe trails as smooth tracks, not stepped
 *     segments.
 *
 * Future visual refinements parked here for traceability:
 *   - Gradient older-to-newer (older points faded, newer brighter)
 *     — MapLibre supports ``line-gradient`` paint spec but requires
 *     the source to expose feature line-progress. M5 keeps the
 *     polyline a single ``LineString`` so the basic ``line-color``
 *     paint suffices; gradient adds source-side complexity (one
 *     feature per segment) deferrable to Open Item 12 resolution.
 *   - Dashed treatment for confidence-degraded — the BFF doesn't
 *     yet surface a confidence field on the trail items; the
 *     visual hook would land alongside that schema extension.
 *
 * # Theme coupling
 *
 * Colors flow through :func:`getStatusColor` so Stream B's dark
 * theme participates without re-mounting the layer; the resolved
 * value is re-read at paint time per the marker-styling pattern.
 *
 * Cross-references:
 *   - REQ-UI-012 (trail rendering).
 *   - REQ-UI-040 / REQ-UI-046 (paired light/dark; semantic
 *     distinguishability).
 *   - ADR-016 §5 (trail-view scope).
 *   - ADR-016 Open Item 12 (visual design — stays OPEN; M5 ships
 *     defaults).
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md.
 */
import { toUnwrappedCoordinates } from "@/map/antimeridian";
import {
  HEALTH_STATE,
  getStatusColor,
  type MarkerStatus,
  healthStateToMarkerStatus,
} from "@/map/marker-styling";
import type { TrailItem } from "@/map/trail-types";
import { hasMapPosition } from "@/map/snapshot-types";

/**
 * MapLibre source ID for the trail GeoJSON. Exported so
 * :file:`trail-layer-impl.ts` + the Vitest layer-mount test + the
 * Playwright e2e + the verified-ui-done agent's
 * ``evaluate_script`` probe can target the source by name.
 *
 * Cross-references:
 *   - REQ-UI-012.
 */
export const TRAIL_SOURCE_ID = "meridian-trail-source";

/**
 * MapLibre layer ID for the trail line. Same exported-constant
 * pattern as :const:`TRAIL_SOURCE_ID`.
 *
 * Cross-references:
 *   - REQ-UI-012.
 */
export const TRAIL_LAYER_ID = "meridian-trail-layer";

/**
 * Polyline width in screen pixels. Storyboard-default; refinement
 * is Open Item 12.
 */
export const TRAIL_LINE_WIDTH_PX = 2;

/**
 * Polyline opacity. < 1 so the trail visually sits behind the
 * marker (which paints at opacity 1) and reads as "past positions"
 * rather than "current state".
 */
export const TRAIL_LINE_OPACITY = 0.8;

/**
 * Derive the trail's status from the most-recent trail item.
 *
 * The trail items are time-ascending per the BFF contract
 * (``packages/meridian_bff/api/openapi.yaml`` §TrailResponse.items
 * "Status records for the requested Producer, time-ascending");
 * the most-recent point's ``health_state`` is the visual anchor
 * because that's what the marker is also painting. Aligning the
 * trail with the marker keeps the visual story coherent — a
 * degraded Producer's trail reads degraded.
 *
 * Empty input returns ``"unknown"`` so the caller still gets a
 * resolvable color (operator-visibility per CLAUDE.md §8.1 — a
 * silent fall-through to undefined would crash the paint spec).
 *
 * Cross-references:
 *   - REQ-UI-012.
 *   - REQ-UI-046.
 */
export function trailStatusFromItems(items: readonly TrailItem[]): MarkerStatus {
  if (items.length === 0) {
    return "unknown";
  }
  // Trail items are time-ascending per the BFF; the most-recent
  // point is the last element. ``!`` justified by the length check
  // above; TS can't see across :func:`Array#length` narrowing.
  const latest = items[items.length - 1]!;
  return healthStateToMarkerStatus(latest.health_state);
}

/**
 * Resolve the MapLibre paint ``line-color`` value for a given
 * status.
 *
 * Reads through :func:`getStatusColor` so the theme seam (REQ-UI-
 * 040) carries through to the trail layer the same way the marker
 * layer participates.
 *
 * Cross-references:
 *   - REQ-UI-012.
 *   - REQ-UI-040 / REQ-UI-046.
 */
export function trailLineColor(status: MarkerStatus): string {
  return getStatusColor(status);
}

/**
 * Convert a sequence of trail items to a MapLibre GeoJSON source
 * payload — a single :class:`FeatureCollection` carrying one
 * :class:`LineString` feature whose coordinates are the trail's
 * positions in time-ascending order.
 *
 * Empty input emits an empty FeatureCollection so the layer can
 * mount with a degenerate payload and update via
 * :func:`maplibregl.GeoJSONSource#setData` as later fetches
 * resolve. MapLibre accepts an empty FeatureCollection without
 * complaint.
 *
 * Why a single LineString (vs. one feature per segment)? The M5
 * brief defers the visual refinement (gradient tails per Open Item
 * 12); a single LineString is the minimum-viable surface that
 * renders a continuous polyline. Future per-segment styling
 * (gradient over line-progress) can split features later — the
 * source schema is additive.
 *
 * Cross-references:
 *   - REQ-UI-012.
 */
export function trailItemsToFeatureCollection(
  items: readonly TrailItem[],
): GeoJSON.FeatureCollection<GeoJSON.LineString | GeoJSON.Point> {
  // Trail rows always carry a position (the BFF trail query excludes NULL-
  // position status), so this filter is defensive type-safety for the now-
  // nullable StatusItem position (REQ-INGEST-044) — drop any location-less row
  // rather than emit a null coordinate.
  const points = items.filter(hasMapPosition);
  if (points.length === 0) {
    return { type: "FeatureCollection", features: [] };
  }
  if (points.length === 1) {
    // A single point isn't a LineString. Emit a Point feature so
    // the source has SOMETHING to render — the layer's filter
    // excludes points (line layers ignore non-LineString features)
    // but the source-data-update flow stays uniform. The
    // eligibility predicate filters this case upstream, but
    // defense-in-depth: never emit an invalid LineString.
    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [points[0]!.longitude_deg, points[0]!.latitude_deg],
          },
          properties: {},
        },
      ],
    };
  }
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          // Antimeridian-unwrapped so a trail crossing the ±180° seam stays
          // continuous instead of drawing across the whole map (REQ-UI-012).
          type: "LineString",
          coordinates: toUnwrappedCoordinates(points),
        },
        properties: {
          // ``producer_id`` on the LineString lets feature-state
          // hooks (selection-pulse, future filtering) target the
          // line without re-querying the source.
          producer_id: points[0]!.producer_id,
        },
      },
    ],
  };
}

/**
 * Sanity-check helper for the test: the design-system tokens cover
 * every status the paint spec might key off. Mirrors the
 * :func:`paintExpressionStatusCoverage` pattern on the snapshot
 * layer.
 *
 * Returns the four health-state-derived statuses that
 * :func:`trailStatusFromItems` can emit. ``stale`` is reserved for
 * a future UI-derived overlay (see :file:`marker-styling.ts` §"--
 * status-stale as a UI-derived state"); not part of the trail
 * paint surface at M5.
 *
 * Cross-references:
 *   - REQ-UI-046.
 */
export function trailPaintStatusCoverage(): readonly MarkerStatus[] {
  return ["healthy", "degraded", "unhealthy", "unknown"];
}

/**
 * The set of HealthState integers :func:`trailStatusFromItems`
 * accepts. Exported so the test can verify exhaustive coverage
 * against the proto enum.
 */
export const TRAIL_HEALTH_STATE_COVERAGE = [
  HEALTH_STATE.UNSPECIFIED,
  HEALTH_STATE.HEALTHY,
  HEALTH_STATE.DEGRADED,
  HEALTH_STATE.OFFLINE,
  HEALTH_STATE.UNKNOWN,
] as const;
